import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { SyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-generate-syllabus';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CompleteCourseDataAI, CompleteSyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-complete-syllabus';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RTEComponent } from '@edtech-ui/shared/editor/rte/rte.component';
import { CourseDataTypeEnum } from '@edtech-ui/constants/course-types.enum';
import { DeleteDialogBoxComponent } from '../../delete-dialog-box/delete-dialog-box.component';
import { MatIconModule } from '@angular/material/icon';
import { UpdateCourseDataAI } from '@edtech-ui/core/models/ai-course/edit-course';
import { UpdateUnitByIdPayload } from '@edtech-ui/core/models/ai-course/update-unit-by-id';
import { UpdateTopicById } from '@edtech-ui/core/models/ai-course/update-topic-by-id';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AddUnit, AddUnitAI } from '@edtech-ui/core/models/ai-course/add-unit';
import { AddTopic, Subtopic } from '@edtech-ui/core/models/ai-course/add-topic';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UnpublishedCourseDialogBoxComponent } from '../unpublished-course-dialog-box/unpublished-course-dialog-box.component';
import { AddTextBookDialogBoxComponent } from '../add-text-book-dialog-box/add-text-book-dialog-box.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { CourseTopicDialogBoxComponent } from '../course-topic-dialog-box/course-topic-dialog-box.component';

@Component({
	selector: 'app-ai-course-result',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, FormsModule, MatIconModule, DragDropModule, MatTooltipModule,
		MatProgressSpinnerModule,MatExpansionModule
	],
	templateUrl: './ai-course-result.component.html',
	styleUrl: './ai-course-result.component.scss'
})
export class AiCourseResultComponent implements OnInit, OnDestroy {

	_syllabusData: SyllabusResponseAI['Data'] | null = null;
	subscription = new Subscription();
	classroomId!: number;
	courseId!: string;
	_CourseDataType = CourseDataTypeEnum;
	courseStatus = CourseStatusEnum;
	isLoadingPublish: boolean = false;
	isLoadingDraft: boolean = false;
	status : CourseStatusEnum|null = null;
	isPublish: boolean = false;

	constructor(private _courseService: CourseManagementService,
		private router: Router,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			const classroomId = params.get('classroomId');
			const courseId = params.get('courseId');
			if (classroomId) {
				this.classroomId = +classroomId;
			}
			if (courseId) {
				this.courseId = courseId;
			}
			this.getCourseDetailsById()
		});
		this.getCompleteCourseData();

		this.subscription.add(
			this._courseService.reload$.subscribe(() => { this.getCourseDetailsById(); }));
	}

	// If we have detailContent then call the API using courseId
	getCourseDetailsById() {
		if (this.courseId && this.classroomId) {
			this.subscription.add(
				this._courseService.getCoursesByCourseId(this.courseId).subscribe({
					next: (response: CompleteSyllabusResponseAI) => {
						if (response.Success && response.Data) {
							this._syllabusData = response.Data;
							this.status= response.Data.status as CourseStatusEnum;
						}
					}
				})
			);
		}
	}

	getCompleteCourseData() {
		this.subscription.add(
			this._courseService.saveSyllabusData.subscribe(data => {
				this._syllabusData = data;
			}
			)
		);
	}

	postAllCourseData(status: CourseStatusEnum) {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is required');
			return;
		}
		if (status === CourseStatusEnum.Draft) {
			this.isLoadingDraft = true;
		} else {
			this.isLoadingPublish = true;
		}

		const payload: CompleteCourseDataAI = {
			description: this._syllabusData?.description || '',
			files: this._syllabusData?.files ?? null,
			use_research_tools: this._syllabusData?.use_research_tools ?? false,
			summary: this._syllabusData?.summary || '',
			instructions_for: this._syllabusData?.instructions_for || '',
			goals: this._syllabusData?.goals || [],
			research: this._syllabusData?.research || '',
			course_duration: this._syllabusData?.course_duration ?? 0,
			course_code: this._syllabusData?.course_code || '',
			course_title: this._syllabusData?.course_title || '',
			grade_level: this._syllabusData?.grade_level || '',
			credits: this._syllabusData?.credits ?? 0,
			internal_marks: this._syllabusData?.internal_marks ?? 0,
			exam_marks: this._syllabusData?.exam_marks ?? 0,
			total_units_count: this._syllabusData?.total_units_count ?? 0,
			course_objectives: this._syllabusData?.course_objectives || [],
			course_outcomes: this._syllabusData?.course_outcomes || [],
			textbooks: this._syllabusData?.textbooks || [],
			web_references_links: this._syllabusData?.web_references_links || [],
			units: this._syllabusData?.units || [],
			status: status,
		};

		if (!this.courseId) {
			this.subscription.add(
				this._courseService.postCompleteSyllbus(payload, this.classroomId, status).pipe(
					tap(response => {
						if (response.Success) {
							this._toastr.success(status === CourseStatusEnum.Draft ? 'Your course has been saved as a draft. You can continue editing any time' : 'Your course has been published and is now available to students!');
							this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
						} else {
							this._toastr.error(response.Message);
						}
					}),
					finalize(()=>{
                    this.isLoadingPublish = false;
					this.isLoadingDraft = false;
				}),
					catchError(() => {
						this.isLoadingPublish = false;
						this.isLoadingDraft = false;
						return of(null);
					})
				).subscribe()
			);
		}
	}

	postUpdateCourse(){
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is required');
			return;
		}
		this.isLoadingPublish = true;
		this.isLoadingDraft = true;

		const payload: UpdateCourseDataAI = {
			course_id: this.courseId,
			course_title: this._syllabusData?.course_title || '',
			course_code: this._syllabusData?.course_code || '',
			course_duration: this._syllabusData?.course_duration || 0,
			total_units_count: this._syllabusData?.total_units_count || 0,
			course_outcomes: this._syllabusData?.course_outcomes || [],
			course_objectives: this._syllabusData?.course_objectives || [],
			textbooks: this._syllabusData?.textbooks || [],
			web_references_links: this._syllabusData?.web_references_links || [],
		}

		this.subscription.add(
			this._courseService.postUpdateCourse(payload, this.courseId,null,null).pipe(
				tap(response => {
					if (response.Success) {
						this.getCourseDetailsById();
						this._toastr.success('Your course has been update successfully');
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoadingPublish = false;
					this.isLoadingDraft = false;
				}),
				catchError(() => {
					this.isLoadingPublish = false;
					this.isLoadingDraft = false;
					return of(null);
				})
			).subscribe()
		);
	}

	deleteUnitById(unitId: string): void {
		if (!this._syllabusData?.units) return;

		if (this.courseId) {
			this.subscription.add(
				this._courseService.deleteUnitById(this.courseId, unitId).pipe(
					tap(response => {
						if (response.Success) {
							this._syllabusData!.units = this._syllabusData!.units!.filter(unit => unit.unit_id !== unitId);
							this._syllabusData!.course_duration = this._syllabusData!.units.reduce(
								(total, unit) => total + (unit.unit_hours || 0), 0
							);
							this.getCourseDetailsById()
							this._toastr.success(response.Message);
						} else {
							this._toastr.error(response.Message);
						}
					})
				).subscribe()
			);
		}
		else {
			const updatedUnits = this._syllabusData.units.filter(unit => unit.unit_id !== unitId)
				.map((unit, index) => ({ ...unit, order: index + 1 }));

			const updatedDuration = updatedUnits.reduce((total, unit) => total + (unit.unit_hours || 0), 0);
			const updatedTotalUnitCount = updatedUnits.length;
			this._syllabusData = {
				...this._syllabusData, units: updatedUnits,
				course_duration: updatedDuration, total_units_count: updatedTotalUnitCount
			};
			this._toastr.success('unit deleted successfully');
		}
	}


	deleteAllObjectives(): void {
		if (this._syllabusData) {
			this._syllabusData.course_objectives = [];
			this._toastr.success('objectives deleted successfully')
		}
	}

	deleteAllCourseOutcome(): void {
		if (this._syllabusData) {
			this._syllabusData.course_outcomes = [];
			this._toastr.success('outcomes deleted successfully')
		}
	}

	deleteAllResources(): void {
		if (this._syllabusData) {
			this._syllabusData.textbooks = [];
			this._syllabusData.web_references_links = [];
			this._toastr.success('resources deleted successfully')
		}
	}


	openEditDialogBox(
		type: CourseDataTypeEnum, data: any) {
		const dialogRef = this.dialog.open(RTEComponent, {
			width: '600px',
			data: { type, payload: data }
		});

		dialogRef.afterClosed().subscribe((updated) => {
			if (!updated || !this._syllabusData) return;

			switch (type) {
				case CourseDataTypeEnum.Unit:
					this.updateThatUnit(updated);
					break;
				case CourseDataTypeEnum.Objectives:
					this._syllabusData.course_objectives = updated;
					if (this.courseId) {
						this.postUpdateCourse();
					}
					break;
				case CourseDataTypeEnum.Outcome:
					this._syllabusData.course_outcomes = updated;
					if (this.courseId) {
						this.postUpdateCourse();
					}
					break;
				case CourseDataTypeEnum.Weblinks:
					this._syllabusData.web_references_links = updated;
					if (this.courseId) {
						this.postUpdateCourse();
					}
					break;
				case CourseDataTypeEnum.Textbooks:
					this._syllabusData.textbooks = updated;
					if (this.courseId) {
						this.postUpdateCourse();
					}
					break;
				case CourseDataTypeEnum.Resources:
					this._syllabusData.web_references_links = updated?.webs;
					this._syllabusData.textbooks = updated?.books;
					break;
				case CourseDataTypeEnum.Code:
					this._syllabusData.course_code = updated;
					if (this.courseId) {
						this.postUpdateCourse();
					}
					break;
				case CourseDataTypeEnum.Course_Title:
					this._syllabusData.course_title = updated;
					if (this.courseId) {
						this.postUpdateCourse();
					}
					break;
				case CourseDataTypeEnum.Course_Unit_Count:
					this._syllabusData.total_units_count = updated;
					break;
				case CourseDataTypeEnum.Course_duration:
					this._syllabusData.course_duration = updated;
					break;
			}
		});
	}


	updateThatUnit(updatedUnit: any) {
		if (!this._syllabusData?.units) return;

		const index = this._syllabusData.units.findIndex(unit => unit.unit_id === updatedUnit.unit_id);
		if (index >= 0) {
			this._syllabusData.units[index] = {
				...this._syllabusData.units[index],
				unit_title: updatedUnit.unit_title,
				unit_hours: Number(updatedUnit.unit_hours) || 0,
			}
			const updatedDuration = this._syllabusData.units.reduce(
				(total, unit) => total + (unit.unit_hours || 0), 0 );
			this._syllabusData = {
				...this._syllabusData,
				course_duration: updatedDuration,
				total_units_count: this._syllabusData.units.length
			};
		}
	}


	editUnitById(unitId: string): void {
		const unit = this._syllabusData?.units?.find(u => u.unit_id === unitId);
		if (!unit) return;

		this.dialog.open(RTEComponent, {
			width: '600px',
			data: { type: 'unit', payload: unit }
		}).afterClosed().subscribe(updatedUnit => {
			if (updatedUnit) {
				this.updateThatUnit(updatedUnit);
				if (this.courseId && updatedUnit) {

					const payload: UpdateUnitByIdPayload = {
						course_id: this.courseId,
						unit_id: updatedUnit.unit_id,
						unit_title: updatedUnit.unit_title,
						unit_hours: Number(updatedUnit.unit_hours) || 0
					};
					this.subscription.add(
						this._courseService.postUpdateUnitById(this.courseId, unitId, payload).pipe(
							tap(response => {
								if (response.Success) {
									this._toastr.success(response.Message);
									this.getCourseDetailsById();
								}
								else {
									this._toastr.error(response.Message);
								}
							})
						).subscribe()
					);
				} else {
					this._toastr.success("unit updated successfully")
				}
			}
		});
	}


	editTopicById(unitId: string, topicId: string): void {
		const unit = this._syllabusData?.units?.find(u => u.unit_id === unitId);
		if (!unit) return;

		const topic = unit.topics.find(t => t.topic_id === topicId);
		if (!topic) return;

		this.dialog.open(CourseTopicDialogBoxComponent, {
			width: '600px',
			data: { type: 'edit', payload: topic }
		}).afterClosed().subscribe(updatedTopic => {
			if (!updatedTopic) return;

			const topicIndex = unit.topics.findIndex(t => t.topic_id === topicId);
			if (topicIndex !== -1) {
				unit.topics[topicIndex] = {
					...unit.topics[topicIndex],
					...updatedTopic
				};
			}

			if (this.courseId) {

				const payload: UpdateTopicById = {
					course_id: this.courseId,
					topic_title: updatedTopic.topic_title,
					subtopics: Array.isArray(updatedTopic.subtopics) ?
						updatedTopic.subtopics.map((s: Subtopic) => ({
							subtopic_title: s.subtopic_title,
							description: s.description || '',
							reference_links: Array.isArray(s.reference_links)
								? s.reference_links.filter((link: string) => !!link?.trim())
								: []
						})) : []
				};

				const topicNo = updatedTopic.topic_no ? updatedTopic.topic_no.toString() : '0';

				this.subscription.add(
					this._courseService.postUpdateTopicById(this.courseId, unitId, topicId, topicNo, payload).pipe(
						tap(response => {
							if (response.Success) {
								this._toastr.success(response.Message);
							} else {
								this._toastr.error(response.Message);
							}
						})
					).subscribe()
				);
			} else {
				this._toastr.success("topic updated successfully");
			}
		});
	}

	openDeleteDialogBox(type: CourseDataTypeEnum, id?: string, topicId?: string) {
		const dialogRef = this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { type }
		});
		dialogRef.afterClosed().subscribe(confirm => {
			if (!confirm) return;

			switch (type) {
				case CourseDataTypeEnum.Unit:
					if (id) {
						this.deleteUnitById(id);
					}
					break;
				case CourseDataTypeEnum.Objectives:
					this.deleteAllObjectives();
					break;
				case CourseDataTypeEnum.Outcome:
					this.deleteAllCourseOutcome();
					break;
				case CourseDataTypeEnum.Resources:
					this.deleteAllResources();
					break;
				case CourseDataTypeEnum.Topic:
					if (id && topicId)
						this.deleteTopicById(id, topicId)
			}
		})
	}

	goToBack() {
		if (this.courseId) {
			this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
		} else {
			this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'ai-creation']);
		}
	}

	onUnitDrop(event: CdkDragDrop<any[]>) {
		if (!this._syllabusData?.units) return;

		moveItemInArray(this._syllabusData.units, event.previousIndex, event.currentIndex);

		this._syllabusData.units = this._syllabusData.units.map((unit, index) => ({
			...unit,
			order: index + 1
		}));

		if (this.courseId) {
			this._syllabusData.units.forEach((unit, index) => {
				const payload: UpdateUnitByIdPayload = {
					course_id: this.courseId!,
					unit_id: unit.unit_id,
					unit_title: unit.unit_title,
					order: index + 1,
					unit_hours: unit.unit_hours,
				};
				this.subscription.add(
					this._courseService.postUpdateUnitById(this.courseId!, unit.unit_id, payload).subscribe());
			});
		}
	}

	onTopicDrop(event: CdkDragDrop<any[]>, unit: any) {

		if (!unit?.topics) return;

		moveItemInArray(unit.topics, event.previousIndex, event.currentIndex);
		unit.topics.forEach((topic: any, index: number) => {
			topic.topic_no = index + 1;
		});

		if (this.courseId) {
			unit.topics.forEach((topic: any, index: number) => {
				const payload: UpdateTopicById = {
					course_id: this.courseId!,
					topic_title: topic.topic_title,
					subtopics: Array.isArray(topic.subtopics) ? topic.subtopics.filter((s: string) => !!s?.trim()) : [],
				};

				const topicId = topic.topic_id;
				const topicNo = (index + 1).toString();

				this.subscription.add(
					this._courseService.postUpdateTopicById(this.courseId!, unit.unit_id, topicId, topicNo, payload).subscribe()
				);
			});
		}
	}


	addUnit(): void {
		const dialogRef = this.dialog.open(RTEComponent, {
			width: '600px',
			data: { type: CourseDataTypeEnum.Unit, payload: { unit_title: '', unit_hours: 0 } }
		});

		dialogRef.afterClosed().subscribe((result: { unit_title: string, unit_hours?: string }) => {
			if (!result?.unit_title?.trim()) return;

			const unitTitle = result.unit_title.trim();
			const unitHour = Number(result.unit_hours) || 0;

			if (this.courseId) {
				const payload: AddUnit = {
					unit_title: unitTitle,
					unit_hours: unitHour,
				};

				this.subscription.add(
					this._courseService.postAddUnit(this.courseId, payload).pipe(
						tap(response => {
							if (response?.Success && response.Data) {
								const unit: AddUnitAI = {
									...response.Data,
									unit_hours: Number(response.Data.unit_hours) || 0
								};

								this._syllabusData?.units.push(unit);
								this._toastr.success(response.Message);
								this.getCourseDetailsById();
							} else {
								this._toastr.error(response.Message);
							}
						})
					).subscribe()
				);

			} else {
				const order = this._syllabusData?.units?.length
					? this._syllabusData.units.length + 1 : 1;

				const newUnit: AddUnitAI = {
					unit_id: 'unit_' + Date.now(),
					unit_title: unitTitle,
					unit_hours: unitHour,
					order,
					topics: []
				};

				this._syllabusData = {
					...this._syllabusData!,
					units: [...(this._syllabusData?.units ?? []), newUnit],
					total_units_count: (this._syllabusData?.units?.length ?? 0) + 1,
					course_duration: (this._syllabusData?.course_duration ?? 0) + unitHour
				};
				this._toastr.success('Unit added successfully');
			}
		});
	}


	addTopic(unit: AddUnitAI): void {
		const dialogRef = this.dialog.open(CourseTopicDialogBoxComponent, {
			width: '600px',
			data: { type: 'add', payload: { topic_title: '' } }
		});

		dialogRef.afterClosed().subscribe((result: { topic_title: string,description?: string, subtopics: Subtopic[] }) => {
			if (!result?.topic_title?.trim()) return;

			const topicTitle = result.topic_title?.trim();
			const subtopics = result.subtopics || [];
			const description = result.description?.trim() || '';

			if (this.courseId) {
				const payload: AddTopic = {
					course_id: this.courseId,
					unit_id: unit.unit_id,
					topic_title: topicTitle,
					subtopics:subtopics,
					description:description,
					order: unit.topics.length + 1
				};
				this.subscription.add(
					this._courseService.postAddtopic(this.courseId, unit.unit_id, payload).pipe(
						tap(response => {
							if (response.Success && response.Data) {
								unit.topics.push(response.Data);
								this._toastr.success(response.Message);
							} else {
								this._toastr.error(response.Message);

							}
						})
					).subscribe()
				);
			} else {
				const newTopic = {
					topic_id: 'topic_' + Date.now(),
					topic_title: topicTitle,
					subtopics,
					description,
					order: unit.topics.length + 1
				};
				unit.topics.push(newTopic);
				this._toastr.success('Topic added successfully');
			}
		});
	}


	deleteTopicById(unitId: string, topicId: string): void {
		if (!this._syllabusData?.units) return;

		const unit = this._syllabusData.units.find(u => u.unit_id === unitId);
		if (!unit || !unit.topics) return;

		const topic = unit.topics.find(t => t.topic_id === topicId);
		if (!topic) return;


		if (this.courseId) {
			this.subscription.add(
				this._courseService.deleteTopicById(this.courseId, unitId, topicId).pipe(
					tap(response => {
						if (response.Success && response.Data) {
							this._toastr.success(response.Message);
							this.getCourseDetailsById()
						} else {
							this._toastr.error(response.Message);
						}
					})
				).subscribe()
			);
		} else {
			unit.topics = unit.topics.filter(topic => topic.topic_id !== topicId)
				.map((topic, index) => ({ ...topic, topic_no: index + 1 }));
			this._toastr.success('Topic deleted successfully');
		}
	}

	openDeleteCourseDialogBox(courseId: string) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { courseId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.deleteCoursesById(courseId)
			}
		});
	}

	deleteCoursesById(courseId: string) {
		this.subscription.add(
			this._courseService.deleteCourseById(courseId).subscribe({
				next: () => {
					this._toastr.success('Course deleted successfully');
					this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
				},
				error: () => {
					this._toastr.error('Failed to delete course');
				}
			})
		);
	}

	openUnpublishDialogBox() {
		this.dialog.open(UnpublishedCourseDialogBoxComponent, {
			width: '400px',
			data: { courseId: this.courseId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.patchPublishedCourseByStatus()
			}
		});
	}

	patchPublishedCourseByStatus() {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is required');
			return;
		}

		this.isPublish = true;
		this.subscription.add(
			this._courseService.patchPublishedCourseByStatus(this.classroomId, this.courseId).pipe(
				tap(response => {
					if (response.Success) {
						this.getCourseDetailsById();
						const courseStatus = response.Data?.status;
						if (courseStatus === CourseStatusEnum.Published) {
						this._toastr.success('Your course has been published and is now available to students');
						this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
						} else if (courseStatus === CourseStatusEnum.Draft) {
							this._toastr.success('Your course was Published and is now moved to Draft.');
						}
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(()=>{this.isPublish = false }),
				catchError(() => {
					this.isPublish = false;
					return of(null);
				})
			).subscribe()
		);
	}

	openTextBookDialogBox(book?: any, index?: number): void {
		const dialogRef = this.dialog.open(AddTextBookDialogBoxComponent, {
			width: '700px',
			data: { textBook: book },
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result) return;

			if (index !== undefined && this._syllabusData?.textbooks) {
				this._syllabusData.textbooks[index] = result;
			}
			else if (this._syllabusData) {
				if (!this._syllabusData.textbooks) this._syllabusData.textbooks = [];
				this._syllabusData.textbooks.push(result);
			}

			if (this.courseId) {
				this.postUpdateCourse();
			}
		});
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

}